import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";

import { userAtom, workosAuthAtom, zitadelOrgAtom } from "~/atoms";
import { isWorkOSAuth } from "~/lib/authProvider";
import { ensureFreshSession } from "~/lib/refreshCoordinator";
import { isDemoMode } from "~/utils/Env";

interface UseSecureImageSrcArgs {
  /**
   * Full production image URL (e.g.
   * `${VITE_IMAGE_API_ENDPOINT}/image/download/...`). Null/undefined until the
   * caller has the path segments. Used by both the WorkOS native path (an
   * `<img src>` with a `?v=authGen` cache-buster + cookie auth) and the legacy
   * Zitadel path (fetched to a blob with a Bearer header).
   */
  downloadUrl?: string | null;
  /**
   * Full demo image URL, resolved against the image API. Used only in demo /
   * mock mode, rendered natively; the encrypted ref in the path is the only
   * credential the demo route needs.
   */
  demoUrl?: string | null;
}

export interface SecureImageSrc {
  /** URL for `<img src>` — a native URL (demo/WorkOS) or a blob URL (legacy). */
  src: string | undefined;
  /** True while the legacy blob fetch is in flight (never on the native paths). */
  isLoading: boolean;
  /** True when the image failed to load — render the placeholder when set. */
  hasError: boolean;
  /** Attach to `<img onError>`; on the WorkOS path it triggers a silent refresh. */
  onError: () => void;
  /** Re-attempt (wire to a placeholder's "refresh" affordance). */
  retry: () => void;
}

/**
 * Resolves an image `src` across the three auth modes so the image components
 * don't each copy the branch (V4 — INS-1395):
 *
 * - **demo / mock**: native `<img src>` = `demoUrl`, unauthenticated.
 * - **WorkOS**: native `<img src>` = `${downloadUrl}?v=${authGen}`; the browser
 *   sends the `meq_at` cookie automatically. `onError` calls
 *   `ensureFreshSession({ confidence: "suspected", reactive: true })` — a
 *   successful reactive refresh bumps `authGen`, which changes the URL and
 *   remounts the `<img>`, so every broken tile re-requests with a fresh cookie.
 * - **legacy Zitadel**: `fetch(downloadUrl, { Authorization, zitadelOrgIdString })`
 *   → blob → object URL (revoked on cleanup). Unchanged from pre-V4.
 *
 * `<img>` render contract for callers:
 * `src && !hasError ? <img src={src} onError={onError}/> : isLoading ? <loader/> : <placeholder onRefresh={retry}/>`
 */
export function useSecureImageSrc({
  downloadUrl,
  demoUrl,
}: UseSecureImageSrcArgs): SecureImageSrc {
  const user = useAtomValue(userAtom);
  const zitadelOrg = useAtomValue(zitadelOrgAtom);
  const { authGen } = useAtomValue(workosAuthAtom);

  const isWorkos = isWorkOSAuth();
  const useBlobPath = !isDemoMode && !isWorkos;

  const [retryCount, setRetryCount] = useState(0);
  const retry = useCallback(() => setRetryCount((c) => c + 1), []);

  // Native src for the demo + WorkOS paths. WorkOS appends `?v=authGen` so a
  // reactive refresh remounts the `<img>`; demo needs no refresh.
  const nativeSrc = useMemo(() => {
    if (isDemoMode) return demoUrl ?? undefined;
    if (isWorkos && downloadUrl) return `${downloadUrl}?v=${authGen}`;
    return undefined;
  }, [demoUrl, downloadUrl, isWorkos, authGen]);

  const [blobUrl, setBlobUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Legacy Zitadel path only — fetch bytes with a Bearer header, render a blob.
  useEffect(() => {
    if (!useBlobPath) return;
    // A row that resolves to no URL has to clear the previous row's blob. The
    // viewer keeps one <img> mounted while arrowing between rows, so bailing
    // out here would leave it rendering the last row that had an image instead
    // of the placeholder, and would strand isLoading on an aborted fetch.
    if (!downloadUrl) {
      setBlobUrl(undefined);
      setIsLoading(false);
      setHasError(false);
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | undefined;
    setBlobUrl(undefined);
    setIsLoading(true);
    setHasError(false);
    fetch(downloadUrl, {
      method: "GET",
      signal: controller.signal,
      headers: {
        ...(user && { Authorization: `Bearer ${user.access_token}` }),
        zitadelOrgIdString: zitadelOrg?.idString ?? "",
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch image");
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setIsLoading(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error("Error fetching image:", error);
        setHasError(true);
        setIsLoading(false);
      });
    // Virtualization / navigation unmounts tiles: abort the in-flight request
    // and release the blob so we don't pile up requests or leak object URLs.
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [useBlobPath, downloadUrl, user, zitadelOrg?.idString, retryCount]);

  // Clear a native-path error when the src changes (authGen bump / retry) so
  // the `<img>` remounts and re-requests.
  useEffect(() => {
    if (!useBlobPath) setHasError(false);
  }, [nativeSrc, useBlobPath, retryCount]);

  const onError = useCallback(() => {
    if (isWorkos && !isDemoMode) {
      // Ambiguous failure (401 vs a genuine 404). ensureFreshSession's
      // `suspected` gate only refreshes past strict expiry; a reactive refresh
      // bumps authGen → src changes → this <img> (and every other broken one)
      // remounts. A genuine 404 leaves the token fresh, so it short-circuits.
      void ensureFreshSession({ confidence: "suspected", reactive: true });
    }
    setHasError(true);
  }, [isWorkos]);

  return {
    src: useBlobPath ? blobUrl : nativeSrc,
    isLoading,
    hasError,
    onError,
    retry,
  };
}
